import { FC, Fragment } from 'react';
import { Bar, Doughnut, Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ChevronDown, ChevronUp } from 'react-feather';
import { DocumentNode } from 'graphql';
import styled from 'styled-components';
import { getChartData } from '../../utils';
import { Flex } from '../shared';

interface ChartProps {
  query?: DocumentNode;
  type: string;
  variables?: string;

  queryData: object;
  formatData: string;
  width?: number;
  height?: number;
  legend?: string;
  options?: string;
}

interface NumberCardProps {
  backgroundColor?: string;
  color?: string;
}

interface DoughnutNumberCardProps {
  backgroundColor?: string;
  color?: string;
}

const NumberCard = styled.div<NumberCardProps>`
  background-color: ${props => props.backgroundColor || '#FFF'};
  border-right: 1px solid #8e8e8e;
  height: 100px;
  text-align: center;
  width: 100px;

  & span {
    color: ${props => props.color || '#000'};
    font-size: 24px;
    font-weight: 600;
    line-height: 40px;
  }

  & p {
    font-size: 0.8em;
  }
`;

const DoughnutNumberCard = styled.div<DoughnutNumberCardProps>`
  background-color: ${props => props.backgroundColor || '#8E8E8E'};
  border-radius: 50px;
  height: 100px;
  width: 100px;
`;

const InnerDoughnutNumberCard = styled.div`
  background-color: #fff;
  border-radius: 42px;
  font-size: 24px;
  font-weight: 600;
  height: 84px;
  line-height: 84px;
  margin: 0 auto;
  position: relative;
  text-align: center;
  top: 8px;
  width: 84px;
`;

const Table = styled.table`
  border: none;
  border-collapse: collapse;
  width: 100%;
  padding: ${props => props.theme.space[2]}px;
  margin-bottom: ${props => props.theme.space[1]}px;
  @media screen and (max-width: 640px) {
    tr {
      th {
        border-top: none;
      }
      th:first-child {
        border-left: none;
      }
      th:last-child {
        border-right: none;
      }
      td:first-child {
        border-left: none;
      }
      td:last-child {
        border-right: none;
      }
    }
  }
`;

const TH = styled.th`
  border: 1px solid #aeaeae;
  font-weight: 600;
`;

const TBody = styled.tbody`
  border: none;
  font-size: 0.8em;
  padding: ${props => props.theme.space[2]}px;
  margin-bottom: ${props => props.theme.space[1]}px;
`;

const TR = styled.tr`
  background-color: ${props => props.theme.colors.white};
  margin-bottom: ${props => props.theme.space[1]}px;
`;

const TD = styled.td`
  border: 1px solid #aeaeae;
  text-align: center;
  padding: ${props => props.theme.space[2]}px;
`;

const Chart: FC<ChartProps> = ({ queryData, formatData, type, legend, options, ...rest }) => {
  const jsonData = JSON.parse(formatData);
  const chartData = getChartData(jsonData, queryData, type);

  const parsedOptions = options && JSON.parse(options);

  if (type === 'bar') {
    return (
      <Bar
        plugins={[ChartDataLabels]}
        data={chartData}
        options={{
          ...parsedOptions,
          plugins: {
            datalabels: {
              color: '#FFF',
              formatter: (value: number, context: any) => {
                return value && value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              },
            },
            legend: { ...(legend && JSON.parse(legend)) },
          },
        }}
        {...rest}
      />
    );
  }

  if (type === 'doughnut') {
    return (
      <Doughnut
        plugins={[ChartDataLabels]}
        data={chartData}
        options={{
          ...parsedOptions,
          plugins: {
            datalabels: {
              color: '#FFF',
              formatter: (value: number, context: any) => {
                return value && value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              },
            },
            legend: {
              ...(legend && JSON.parse(legend)),
            },
          },
        }}
        {...rest}
      />
    );
  }

  if (type === 'pie') {
    return (
      <Pie
        plugins={[ChartDataLabels]}
        data={chartData}
        options={{
          ...parsedOptions,
          plugins: {
            datalabels: {
              color: '#FFF',
              formatter: (value: number, context: any) => {
                return value && value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              },
            },
            legend: { ...(legend && JSON.parse(legend)) },
          },
        }}
        {...rest}
      />
    );
  }

  if (type === 'number_card') {
    return (
      <NumberCard backgroundColor={chartData.datasets[0].backgroundColor} color={chartData.datasets[0].color}>
        <span>{chartData.datasets[0].data[0]}</span>
        <p>{chartData.datasets[0].label}</p>
      </NumberCard>
    );
  }

  if (type === 'doughnut_number_card') {
    return (
      <Fragment>
        <Flex size="1" direction="row">
          <Flex size="2" direction="column">
            <h3>{chartData.datasets[0].label}</h3>
            <span>
              {chartData.datasets[0].data[1] >= 0 ? (
                <ChevronUp color="rgb(157,189,45)" />
              ) : (
                <ChevronDown color="rgb(190,48,48)" />
              )}
              {` ${chartData.datasets[0].data[1]} up from last month.`}
            </span>
          </Flex>
          <Flex size="1" direction="row">
            <DoughnutNumberCard
              backgroundColor={chartData.datasets[0].backgroundColor}
              color={chartData.datasets[0].color}
            >
              <InnerDoughnutNumberCard>
                <span>{chartData.datasets[0].data[0]}</span>
              </InnerDoughnutNumberCard>
            </DoughnutNumberCard>
          </Flex>
        </Flex>
      </Fragment>
    );
  }

  if (type === 'doughnut_percentage_card') {
    const newChartData = {
      ...chartData,
      datasets: [
        {
          ...chartData.datasets[0],
          data: [chartData.datasets[0].data[0], 100 - chartData.datasets[0].data[0]],
        },
      ],
    };

    return (
      <Fragment>
        <Flex size="1" direction="column" mr={4}>
          <Doughnut
            plugins={[ChartDataLabels]}
            data={newChartData}
            options={{
              ...parsedOptions,
              plugins: {
                datalabels: {
                  color: '#FFF',
                  formatter: (value: number, context: any) => {
                    if (context.dataIndex === 1) return '';

                    return value && `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}%`;
                  },
                },
                legend: { ...(legend && JSON.parse(legend)) },
              },
            }}
            {...rest}
          />
          <span style={{ fontSize: '0.8em', textAlign: 'center' }}>{newChartData.datasets[0].label}</span>
        </Flex>
      </Fragment>
    );
  }

  if (type === 'doughnut_percentage_change_card') {
    const newChartData = {
      ...chartData,
      datasets: [
        {
          ...chartData.datasets[0],
          data: [chartData.datasets[0].data[0], 100 - chartData.datasets[0].data[0], chartData.datasets[0].data[0]],
        },
      ],
    };

    return (
      <Fragment>
        <Flex size="1" direction="row">
          <Flex size="2" direction="column">
            <h3>{newChartData.datasets[0].label}</h3>
            <span>
              {newChartData.datasets[0].data[1] >= 0 ? (
                <ChevronUp color="rgb(157,189,45)" />
              ) : (
                <ChevronDown color="rgb(190,48,48)" />
              )}
              {` ${newChartData.datasets[0].data[2]} up from last month.`}
            </span>
          </Flex>
          <Flex size="1" direction="column" mr={4}>
            <Doughnut
              plugins={[ChartDataLabels]}
              data={newChartData}
              options={{
                ...parsedOptions,
                plugins: {
                  datalabels: {
                    color: '#FFF',
                    formatter: (value: number, context: any) => {
                      if (context.dataIndex === 1) return '';

                      return value && `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}%`;
                    },
                  },
                  legend: { ...(legend && JSON.parse(legend)) },
                },
              }}
              {...rest}
            />
            <span style={{ fontSize: '0.8em', textAlign: 'center' }}>{newChartData.datasets[0].label}</span>
          </Flex>
        </Flex>
      </Fragment>
    );
  }

  if (type === 'table') {
    return (
      <Table>
        <thead>
          <tr>
            {chartData.labels.map((label: string, index: number) => (
              <TH key={index}>{label}</TH>
            ))}
          </tr>
        </thead>
        <TBody>
          {chartData.datasets.length &&
            Array.isArray(chartData.datasets[0].data[0]) &&
            chartData.datasets[0].data[0].map((item: any, index: number) => {
              return (
                <TR key={index}>
                  {chartData.labels.includes('#') ? <TD>{index + 1}. </TD> : null}
                  {Object.keys(item).map((field: string, ind: number) => {
                    return <TD key={ind}>{item[field]}</TD>;
                  })}
                </TR>
              );
            })}
          {chartData.datasets.length &&
            chartData.datasets[0].data[0].constructor === {}.constructor &&
            Object.keys(chartData.datasets[0].data[0]).map((item: any, index: number) => {
              if (item === '__typename') return null;

              return (
                <TR key={index}>
                  <TD>{item.replace(/\b[a-z]/g, (x: any) => x.toUpperCase())}</TD>
                  <TD>{chartData.datasets[0].data[0][item] ? chartData.datasets[0].data[0][item].target : 0}</TD>
                  <TD>{chartData.datasets[0].data[0][item] ? chartData.datasets[0].data[0][item].actual : 0}</TD>
                </TR>
              );
            })}
        </TBody>
      </Table>
    );
  }

  return null;
};

export default Chart;
